import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Building2,
  Phone,
  User,
  ShieldCheck,
  Zap,
  Clock,
  Loader2,
} from 'lucide-react';
import { submitContactInquiry } from '../services/api';
import { PlanType } from '../types';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedPlan?: PlanType | string;
  domain?: string;
  defaultEmail?: string | null;
  defaultName?: string | null;
}

export const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  preselectedPlan,
  domain = 'cerebrolabs.io',
  defaultEmail = '',
  defaultName = '',
}) => {
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string>(preselectedPlan || 'team10');
  const [message, setMessage] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (preselectedPlan) {
      setSelectedPlan(preselectedPlan);
      setMessage(
        `Dzień dobry, chcielibyśmy aktywować pakiet "${preselectedPlan.toUpperCase()}" dla naszej organizacji (${domain}). Proszę o kontakt w sprawie oferty i faktury.`
      );
    } else {
      setMessage(`Dzień dobry, prosimy o kontakt w sprawie wdrożenia centralnych stopek CerebroSign dla domeny ${domain}.`);
    }
  }, [preselectedPlan, domain]);

  useEffect(() => {
    if (defaultEmail && !email) setEmail(defaultEmail);
    if (defaultName && !name) setName(defaultName);
  }, [defaultEmail, defaultName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setErrorMessage('Wprowadź imię i nazwisko oraz adres e-mail.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await submitContactInquiry({
        name,
        email,
        phone,
        company: company || domain,
        plan: selectedPlan,
        message,
        domain,
      });

      if (result.success) {
        setIsSuccess(true);
      } else {
        setErrorMessage(result.message || 'Wystąpił problem z wysyłką formularza.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Nie udało się wysłać wiadomości.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setIsSuccess(false);
    setErrorMessage(null);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 8, 16, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={handleResetAndClose}
    >
      <div
        className="card"
        style={{
          maxWidth: 560,
          width: '100%',
          padding: 0,
          borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.25), 0 0 35px rgba(0, 102, 255, 0.12)',
          overflow: 'hidden',
          animation: 'fadeIn 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Header */}
        <div
          style={{
            background: 'linear-gradient(180deg, rgba(0, 102, 255, 0.08) 0%, transparent 100%)',
            borderBottom: '1px solid #e2e8f0',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(0, 102, 255, 0.12)',
                border: '1px solid rgba(0, 102, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0066FF',
              }}
            >
              <Mail size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0B192C', margin: 0 }}>
                {preselectedPlan && preselectedPlan !== 'free'
                  ? `Aktywacja pakietu ${preselectedPlan.toUpperCase()}`
                  : 'Kontakt z zespołem CerebroSign'}
              </h3>
              <p style={{ fontSize: 12, color: '#475569', margin: '3px 0 0 0' }}>
                Wysyłka zapytania do biuro@cerebrolabs.pl • Odpowiedź w 24h
              </p>
            </div>
          </div>

          <button
            onClick={handleResetAndClose}
            className="btn btn-outline"
            style={{ padding: 6, borderRadius: '50%', color: '#64748b' }}
            aria-label="Zamknij okno"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '24px' }}>
          {isSuccess ? (
            <div style={{ textAlign: 'center', padding: '24px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '2px solid #10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#059669',
                }}
              >
                <CheckCircle2 size={36} />
              </div>
              <div>
                <h4 style={{ fontSize: 18, fontWeight: 700, color: '#0B192C', marginBottom: 6 }}>
                  Zapytanie zostało wysłane!
                </h4>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, maxWidth: 420 }}>
                  Dziękujemy za kontakt. Wiadomość została bezpiecznie przekazana na adres <strong>biuro@cerebrolabs.pl</strong>. Skontaktujemy się z Tobą na adres <strong>{email}</strong> najszybciej jak to możliwe.
                </p>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: '#0066FF',
                  background: 'rgba(0, 102, 255, 0.08)',
                  padding: '8px 16px',
                  borderRadius: 20,
                  border: '1px solid rgba(0, 102, 255, 0.20)',
                }}
              >
                <Clock size={14} />
                <span>Średni czas odpowiedzi: poniżej 2 godzin w dni robocze</span>
              </div>

              <button
                type="button"
                onClick={handleResetAndClose}
                className="btn btn-primary"
                style={{ marginTop: 8, padding: '10px 24px', fontWeight: 600 }}
              >
                Zamknij okno
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {errorMessage && (
                <div
                  style={{
                    background: 'rgba(239, 68, 68, 0.10)',
                    border: '1px solid #ef4444',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#dc2626',
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <AlertCircle size={16} />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Row 1: Name & Email */}
              <div className="modal-grid-2">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <User size={13} color="#0066FF" />
                    Imię i Nazwisko *
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="np. Jan Kowalski"
                    className="input-control"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Mail size={13} color="#0066FF" />
                    Adres E-mail *
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jan@twojadomena.pl"
                    className="input-control"
                  />
                </div>
              </div>

              {/* Row 2: Phone & Company / Domain */}
              <div className="modal-grid-2">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Phone size={13} color="#0066FF" />
                    Numer Telefonu
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+48 600 000 000"
                    className="input-control"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Building2 size={13} color="#0066FF" />
                    Firma / Domena Google
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder={domain || 'twojadomena.pl'}
                    className="input-control"
                  />
                </div>
              </div>

              {/* Row 3: Plan Selector */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Zap size={13} color="#0066FF" />
                  Interesujący Pakiet
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="input-control"
                  style={{ cursor: 'pointer' }}
                >
                  <option value="free">Solo (Freemium) — 0 PLN</option>
                  <option value="team5">Team 5 (Do 5 skrzynek) — 25 PLN netto/mc</option>
                  <option value="team10">Team 10 (Do 10 skrzynek) — 45 PLN netto/mc</option>
                  <option value="business25">Business 25 (Do 25 skrzynek) — 89 PLN netto/mc</option>
                  <option value="enterprise">Enterprise (&gt; 25 skrzynek / Dedykowane wdrożenie)</option>
                  <option value="custom">Inne zapytanie / Indywidualny szablon HTML</option>
                </select>
              </div>

              {/* Row 4: Message */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
                  Treść wiadomości / Dodatkowe uwagi
                </label>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="input-control"
                  style={{ resize: 'vertical', minHeight: 70 }}
                />
              </div>

              {/* Guarantees Bar */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 11,
                  color: '#475569',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ShieldCheck size={13} color="#059669" />
                  Poufność RODO & Faktura VAT
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Zap size={13} color="#0066FF" />
                  Powered by Resend.com
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="btn btn-outline"
                  style={{ padding: '9px 18px' }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                  style={{
                    padding: '9px 22px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      <span>Wysyłanie...</span>
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>Wyślij zapytanie</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
